import { useIntl } from 'react-intl';

import { RootState } from '../../../../reducers';

import * as CustomerActions from '../../../../actions/customer';
import { CSVLink } from 'react-csv';
import { ChangeEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@material-ui/core';
import { CustomerRouteParams } from '../../../../pages/customer/models';
import { FormattedMessage } from 'react-intl';
import { WikusButton } from '@wikus-ui/components/core/button';
import { WikusItem } from '@wikus-ui/components/core/spacing';
import { WikusPanel } from '@wikus-ui/components/core/card';
import { WikusSwitch } from '@wikus-ui/components/core/switch';
import { kebabCase } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import { unwrapCustomerState } from '../../../../utils/url';
import { useActions } from '../../../../actions';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
});

export const CustomerConsignmentWarehouseOldArticles = () => {
  const intl = useIntl();
  const classes = useStyles();
  const { id: customerId } = useParams<CustomerRouteParams>();
  const customerUserActions: typeof CustomerActions = useActions(CustomerActions);
  const customerOldStock = useSelector((state: RootState) => unwrapCustomerState(state.customerOldStock));
  const [months, setMonths] = useState(6);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    if (customerId) {
      customerUserActions.fetchOldStock(customerId, months);
    }
  }, [customerId, months]);

  const handleChangePage = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleHidden = useCallback(async (itemId: number, eventId: number, charge: boolean) => {
    await customerUserActions.toggleArrivalConfirmationState(customerId, itemId, eventId, charge);
  }, [customerId])

  return (
    <>
      <WikusPanel
        title={intl.formatMessage({ id: 'customers.tabs.consignmentWarehouse.oldArticles' })}
        description={customerOldStock ? intl.formatMessage({ id: 'before' }, {
          date: new Date(customerOldStock.end).toLocaleDateString(),
        }) : undefined}
      >
        {!customerOldStock ? <CircularProgress style={{ width: '25px', height: '25px' }} color="inherit" /> : null}
        {customerOldStock ? <>
          <WikusItem spacingDirection='down'>
            <Grid container spacing={2}>
              <Grid item>
                <WikusButton
                  color="primary"
                  variant={months === 60 ? 'contained' : 'outlined'}
                  onClick={() => setMonths(60)}>
                  <FormattedMessage id="months.older.60" />
                </WikusButton>
              </Grid>
              <Grid item>
                <WikusButton
                  color="primary"
                  variant={months === 24 ? 'contained' : 'outlined'}
                  onClick={() => setMonths(24)}>
                  <FormattedMessage id="months.older.24" />
                </WikusButton>
              </Grid>
              <Grid item>
                <WikusButton
                  color="primary"
                  variant={months === 10 ? 'contained' : 'outlined'}
                  onClick={() => setMonths(10)}>
                  <FormattedMessage id="months.older.10" />
                </WikusButton>
              </Grid>
              <Grid item>
                <WikusButton
                  color="primary"
                  variant={months === 12 ? 'contained' : 'outlined'}
                  onClick={() => setMonths(12)}>
                  <FormattedMessage id="months.older.12" />
                </WikusButton>
              </Grid>
              <Grid item>
                <WikusButton
                  color="primary"
                  variant={months === 6 ? 'contained' : 'outlined'}
                  onClick={() => setMonths(6)}>
                  <FormattedMessage id="months.older.6" />
                </WikusButton>
              </Grid>
            </Grid>
          </WikusItem>
          {customerOldStock.items.length ? <WikusItem spacingDirection="down">
            <CSVLink
              data={customerOldStock.items}
              filename={`${kebabCase(customerId)}.csv`}>
              <WikusButton color='primary'>Download CSV</WikusButton>
            </CSVLink>
          </WikusItem> : null}
          <WikusItem spacingDirection="down">
            <TableContainer component={Paper}>
              <Table stickyHeader size="small" className={classes.table} aria-label="simple table">
                {customerOldStock.items?.length === 0 ? <caption>Keine Einträge gefunden!</caption> : null}
                <TableHead>
                  <TableRow>
                    <TableCell>Customer number</TableCell>
                    <TableCell align="right">Article group</TableCell>
                    <TableCell align="right">Article number</TableCell>
                    <TableCell align="right">Description</TableCell>
                    <TableCell align="right">Coil ID</TableCell>
                    <TableCell align="right">Original amount (m)</TableCell>
                    <TableCell align="right">Stock (m)</TableCell>
                    <TableCell align="right">Date</TableCell>
                    <TableCell align="right">Last withdrawal</TableCell>
                    <TableCell align="right">Charged</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerOldStock.items
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow key={kebabCase(`${row.customerNumber}__${row.articleNumber}__${row.amount}__${row.coilId ?? 'band'}`)}>
                        <TableCell component="th" scope="row">
                          {row.customerNumber}
                        </TableCell>
                        <TableCell align="right">{row.articleGroup}</TableCell>
                        <TableCell align="right">{row.articleNumber}</TableCell>
                        <TableCell align="right">{row.articleDescription}</TableCell>
                        <TableCell align="right">{row.coilId}</TableCell>
                        <TableCell align="right">{row.amount}</TableCell>
                        <TableCell align="right">{row.stock}</TableCell>
                        <TableCell align="right">{new Date(row.date).toLocaleDateString()}</TableCell>
                        <TableCell align="right">{row.lastWithdrawal ? new Date(row.lastWithdrawal).toLocaleDateString() : '-'}</TableCell>
                        <TableCell align="right">
                          <WikusSwitch
                            name={`${row.eventId}-hidden`}
                            checked={row.charged}
                            onChange={(_, hidden) => toggleHidden(row.itemId, row.eventId, hidden)} />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              count={customerOldStock.items.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </WikusItem>
        </> : null}
      </WikusPanel>
    </>
  );
};
